export interface CoachBuildManifest {
  gitSha: string;
  shortSha: string;
  builtAt: string;
  environment: string;
  source: string;
  version: string;
}

const BUILD_INFO_QUERY_PARAM = "buildInfo";
const BUILD_INFO_STORAGE_KEY = "arc.coach.buildInfo";

export function shouldShowCoachBuildStamp(
  search: string,
  storedValue: string | null,
): boolean {
  const params = new URLSearchParams(search);
  const queryValue = params.get(BUILD_INFO_QUERY_PARAM);
  if (queryValue && ["1", "true", "yes"].includes(queryValue.toLowerCase())) {
    return true;
  }
  return storedValue === "1";
}

export function readBuildStampPreference(locationHref?: string): boolean {
  if (typeof window === "undefined") return false;
  let storedValue: string | null = null;
  try {
    storedValue = window.localStorage?.getItem(BUILD_INFO_STORAGE_KEY) ?? null;
  } catch {
    storedValue = null;
  }
  const search = locationHref
    ? new URL(locationHref, window.location.origin).search
    : window.location.search;
  return shouldShowCoachBuildStamp(search, storedValue);
}

export async function fetchCoachBuildManifest(): Promise<CoachBuildManifest> {
  const response = await fetch("/version.json", {
    cache: "no-store",
    headers: {
      Accept: "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`Could not load build manifest (${response.status})`);
  }
  const payload = (await response.json()) as Partial<CoachBuildManifest>;
  if (
    !payload ||
    typeof payload.gitSha !== "string" ||
    typeof payload.shortSha !== "string" ||
    typeof payload.builtAt !== "string" ||
    typeof payload.environment !== "string" ||
    typeof payload.source !== "string" ||
    typeof payload.version !== "string"
  ) {
    throw new Error("Build manifest is missing required fields.");
  }
  return payload as CoachBuildManifest;
}

export function summarizeBuildEnvironment(environment: string): string {
  if (environment === "production") return "prod";
  if (environment.startsWith("preview:")) return `preview:${environment.slice("preview:".length)}`;
  if (environment === "development") return "dev";
  return environment;
}

export function formatCoachBuildStamp(manifest: CoachBuildManifest): string {
  return `${manifest.shortSha} · ${summarizeBuildEnvironment(manifest.environment)}`;
}

export function formatCoachBuildDetail(manifest: CoachBuildManifest): string {
  const builtLabel = new Date(manifest.builtAt).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `Built ${builtLabel} · ${manifest.source}`;
}
